import { el, btn, backButton } from '@/ui/dom';
import { SP_COIN_SVG } from '@/ui/currencyIcons';
import {
  cosmeticsFor,
  cosmeticById,
  RARITY_META,
  MOD_KITNUMBER_ID,
  MOD_TIFO_ID,
  type CosmeticItem,
} from '@/config/cosmetics';
import { SP_PACKS_ON_SALE } from '@/config/spPacks';
import {
  spBalance,
  purchaseCosmetic,
  equipCosmetic,
  equippedPaintJob,
  unequipPaintJob,
  loadoutFor,
  equipMod,
  equippedMods,
  clearMods,
  setKitNumber,
  kitNumberFor,
  setTifoStyle,
  tifoStyleFor,
} from '@/core/skillPoints';
import { catTab } from '@/ui/catTabs';
import { renderNumberChips, renderStyleChips } from '@/ui/modPickers';
import { resolveLook } from '@/entities/carLook';
import { GarageStage } from '@/ui/garage/GarageStage';
import { fauxCentreX, portraitCarAnchor, portraitWheelAnchor } from '@/ui/garage/dockLayout';
import { vehicleLabel } from '@/ui/vehicleCatalog';
import {
  cosmeticCardArt,
  cosmeticTypeTag,
  PAINTJOB_FILTERS,
  paintJobItems,
  type PaintJobFilter,
} from '@/ui/cosmeticArt';
import { animateCount } from '@/ui/SkillPointsToast';
import { sfxUiTick, sfxCollect } from '@/audio/sfx';
import type { SaveData } from '@/core/SaveData';
import { subscribeViewport, type ViewportUnsubscribe } from '@/ui/viewport';
import { lockCta, BUY_GAME_LABEL } from '@/ui/lockCta';
import { promoPanel, type PromoPanel } from '@/ui/promoPanel';
import type { VehicleLoadout } from '@/core/SaveData';

/** M139 — the promo showcase (signed-out web): looks cycled onto the car,
 *  each built around the item it features. Ids are checked against the
 *  catalog at runtime, so a retired id simply drops out of the reel. */
interface PromoLook {
  loadout: VehicleLoadout;
  feature: string;
}
const PROMO_REEL: Record<'shop' | 'customize', readonly PromoLook[]> = {
  shop: [
    { loadout: { paint: 'paint.blaugrana', tires: 'tires.copa' }, feature: 'paint.blaugrana' },
    { loadout: { wrap: 'wrap.brazil', tires: 'tires.king' }, feature: 'wrap.brazil' },
    {
      loadout: { paint: 'paint.partenopei', tires: 'tires.goldenboot' },
      feature: 'tires.goldenboot',
    },
    { loadout: { paint: 'paint.oranje', tires: 'tires.volt' }, feature: 'paint.oranje' },
    { loadout: { wrap: 'wrap.japan', tires: 'tires.quicksilver' }, feature: 'wrap.japan' },
    {
      loadout: { paint: 'paint.lesbleus', tires: 'tires.crimson', mods: ['mod.goalwing'] },
      feature: 'mod.goalwing',
    },
  ],
  customize: [
    {
      loadout: { paint: 'paint.rossonero', tires: 'tires.predator', mods: ['mod.floods'] },
      feature: 'mod.floods',
    },
    {
      loadout: { wrap: 'wrap.argentina', tires: 'tires.azure', mods: ['mod.tifo'] },
      feature: 'mod.tifo',
    },
    {
      loadout: { paint: 'paint.canarinho', tires: 'tires.king', mods: ['mod.trophy', 'mod.flags'] },
      feature: 'mod.trophy',
    },
    {
      loadout: {
        paint: 'paint.xeneize',
        tires: 'tires.goldenboot',
        mods: ['mod.goalwing', 'mod.cleats'],
      },
      feature: 'mod.cleats',
    },
    {
      loadout: { wrap: 'wrap.portugal', tires: 'tires.venom', mods: ['mod.fans'] },
      feature: 'mod.fans',
    },
    {
      loadout: { paint: 'paint.merengue', tires: 'tires.quicksilver', mods: ['mod.rally'] },
      feature: 'mod.rally',
    },
  ],
};
/** Seconds each featured look holds the car. */
const PROMO_LOOK_MS = 2800;

/**
 * ShopScreen.ts — THE SHOP (M79), relaid out M113 to the owner's spec: the
 * car sits prominent on the LEFT with the item detail + action docked beside
 * it, and a full-height glass panel docks down the RIGHT carrying big
 * icon-tile category tabs, the SORT row and a scrollable GRID of item cards
 * (three columns; two in portrait, where the panel becomes a bottom sheet and
 * the car takes the top of the screen). Everything else is unchanged M79/M87
 * behaviour: rarity frames, STOCK leads each slot, live previewLook on the
 * browsed card, two-tap BUY then gold CONFIRM, green EQUIP, the M110
 * buyLocked account gate, the purchase pop, per-tab camera zoom, SP (Shop
 * Points) only — deliberately NO real-money path anywhere.
 *
 * Reached from the Garage. All state changes go through the pure helpers in
 * core/skillPoints.ts (purchase/equip) and are handed back to Flow via
 * onSave — this screen never touches storage itself.
 */

export interface ShopCallbacks {
  onSave: (save: SaveData) => void;
  onBack: () => void;
  /** M110 (owner rule): BUYING requires a Racer ID — a signed-out device
   *  can browse and EQUIP what it owns, but the BUY button becomes a
   *  CREATE RACER ID call-to-action. A CALLBACK (not a snapshot) so the gate
   *  tracks the live account state: the account layer resolves ~a second
   *  after boot, and a shop opened during that window must not stay locked
   *  for a signed-in player. Absent / returns false = buying allowed. */
  buyLocked?: () => boolean;
  /** Where the locked BUY button sends the player (the account screen). */
  onCreateAccount?: () => void;
  /** Owner round (2026-10-02): the shop is part of the paid game and this
   *  player does not own it. They can browse and preview every item; the
   *  shop wears the lock banner and every BUY becomes BUY THE GAME. Live
   *  callback, like buyLocked (ownership resolves after boot). */
  gameLock?: () => { line: string; price: string; onBuy: () => void } | null;
  /** M139 (owner: "logged-out web users can't access the shop or customize
   *  in a way that lets them do anything besides buy the game — a
   *  promotional view, the garage strategy"). Present = the PROMO face: the
   *  item grid, tabs, wallet and every action are gone; the car cycles a
   *  reel of featured looks under the big buy panel. `focus` picks the reel
   *  and copy (the Shop's or Customize's). Read once at show(). */
  promo?: () => {
    focus: 'shop' | 'customize';
    kicker: string;
    headline: string;
    sub?: string;
    price: string;
    onBuy: () => void;
    onLogIn?: () => void;
  } | null;
  /** M130 — BUY SP: present only where SP packs may be sold on this surface
   *  (web, not Big Kid, accounts on — Flow decides; the native app gets
   *  nothing until the store IAP lands in M2, Apple 3.1.1). Called with the
   *  pack id; resolves to an error line to show, null when the page is
   *  handing off to checkout (web), or — M133, inside the store app — the
   *  outcome of the store's own purchase sheet. M134: `onPaid` is called the
   *  moment the store confirms the payment, before the points have arrived. */
  onBuySp?: (packId: string, onPaid?: () => void) => Promise<BuySpResult>;
  /** M133 — the STORE's localised prices per pack id. Present only inside
   *  the store app: the sheet then shows these (Apple requires the real
   *  storefront price) and hides any pack the store does not offer. */
  spStorePrices?: (packIds: readonly string[]) => Promise<Record<string, string>>;
}

/** null = handing off to web checkout · string = an error line (nothing was
 *  charged) · done/cancelled = the in-app purchase sheet closed (M133) ·
 *  pending (M134) = paid, or possibly paid, and the points are not in the
 *  wallet yet: `note` is the plain line to show while they are on the way,
 *  `paid` = the store confirmed the payment (green) rather than the app only
 *  being unable to rule it out (amber — never dressed as a success). */
export type BuySpResult =
  | string
  | null
  | { status: 'done' | 'cancelled' }
  | { status: 'pending'; note: string; paid: boolean };

/** 'paintjobs' = the merged kits+flags body slot (M87). */
type ShopTab = 'paintjobs' | 'tires' | 'mods';

/** M128c: the tabs wear the home-page menu-card language (catTabs.ts). */
const TABS: { key: ShopTab; label: string; sub: string }[] = [
  { key: 'paintjobs', label: 'PAINT JOBS', sub: 'KITS & FLAGS' },
  { key: 'tires', label: 'TIRES & RIMS', sub: 'BOOT COLOURS' },
  { key: 'mods', label: 'MODS', sub: 'BOLT-ONS' },
];

/* ---- M113/M114/M115 stage anchors — the car prominent on the LEFT beside the
 * docked right panel (landscape/desktop) or up TOP over the bottom sheet
 * (portrait). Bigger yFrac = nearer/larger; `zoom` narrows the fov (M113b).
 *
 * M115 (owner round): on PHONES the car was too small on PAINT JOBS — "the
 * current position and zoom of Tires & Rims is the appropriate size and
 * position for Paint Jobs". So the phone paint-job anchors adopt the old tyre
 * framing's scale and placement, while keeping the look-at at body height so
 * the WHOLE car (roof to wheels) still frames above the dock. TIRES & RIMS on
 * phones became something else entirely: a still, ultra-tight close-up of ONE
 * wheel (see PHONE_WHEEL below), so the old zoomT/lookY pair only serves the
 * tall-landscape + desktop layouts now. Every number tuned live in a browser.
 */
const CAR_LAND = { x: 0.27, y: 0.68, zoom: 1.12, xT: 0.27, yT: 0.6, zoomT: 2.2 };
const CAR_SHORT = { x: 0.3, y: 0.6, zoom: 2.0, xT: 0.3, yT: 0.6, zoomT: 2.1 }; // ≤500px-high landscape
/* Portrait: the compact one-row dock pins on the sheet (~y 0.47) — the car
 * fills the zone above it (a stacked dock buried the car; a far anchor
 * shrank it to a speck — both measured at 450x775). */
const CAR_PORTRAIT = { x: 0.5, y: 0.47, zoom: 2.5, xT: 0.5, yT: 0.48, zoomT: 2.7 };
/** TIRES & RIMS aims the camera at wheel-hub height (the default is 1.3). */
const WHEEL_LOOK_Y = 0.62;
/** M115 — the PHONE tyre close-up: which viewport fraction the hub lands on
 *  and how much of the viewport HEIGHT the tyre's diameter eats. Landscape
 *  puts it in the clear zone left of the docked panel; portrait centres it in
 *  the band above the bottom sheet. */
/* M128c (owner: "slightly zoom it out, it's a bit too big"): fill 0.72 → 0.65
 * and 0.34 → 0.31, about a tenth further back on both phone layouts. */
const PHONE_WHEEL_SHORT = { xFrac: 0.29, yFrac: 0.52, fill: 0.65 };
/* (portrait: dockLayout.ts portraitWheelAnchor — derived from the live dock edge, M132) */
/* M115 (owner, desktop): the car should look CENTRED in the clear zone left of
 * the docked item panel — equal gap screen-edge→car and car→panel. That maths
 * lives in ui/garage/dockLayout.ts because the CUSTOMIZE overlay now shares
 * the exact same composition. */
/** The car is off the painted turntable here — keep a soft grounding ring. */
const SHOP_RING_SCALE = 0.3;

export class ShopScreen {
  private readonly root: HTMLDivElement;
  private readonly stageHost: HTMLDivElement;
  private stage: GarageStage | null = null;
  private readonly balanceEl: HTMLSpanElement;
  private readonly dockEl: HTMLDivElement;
  private readonly rideEl: HTMLDivElement;
  private readonly tabsEl: HTMLDivElement;
  private readonly railEl: HTMLDivElement;
  private panelEl!: HTMLDivElement;
  private promo: PromoPanel | null = null;
  private promoTimer: number | null = null;
  private promoOn = false;
  private titleEl!: HTMLDivElement;
  private lockEl: HTMLDivElement | null = null;
  private readonly detailName: HTMLDivElement;
  private readonly detailType: HTMLSpanElement;
  private readonly detailRarity: HTMLSpanElement;
  private readonly detailDesc: HTMLDivElement;
  private readonly actionBtn: HTMLButtonElement;
  private readonly filtersEl: HTMLDivElement;
  /** M128: the Kit Number picker (1-11) in the detail dock. */
  private readonly numPickEl: HTMLDivElement;
  /** M128: the number picked while browsing; stored on buy (or at once when owned). */
  private pendingNumber: number | null = null;
  /** M128c: the Fan Tifo design picked while browsing (lands with the buy). */
  private pendingStyle: number | null = null;

  private cb: ShopCallbacks | null = null;
  private save: SaveData | null = null;
  private tab: ShopTab = 'paintjobs';
  /** PAINT JOBS inspiration filter (ALL · KITS · FLAGS). */
  private filter: PaintJobFilter = 'all';
  /** The browsed item (null = the STOCK card). */
  private selected: string | null = null;
  private confirmArmed = false;
  private confirmTimer: number | null = null;
  private cancelCount: () => void = () => {};
  private popEl!: HTMLDivElement;
  private popTimer: number | null = null;
  private unsubViewport: ViewportUnsubscribe | null = null;
  private readonly onWindowResize = (): void => {
    if (this.root.style.display === 'none') return;
    this.stage?.resize();
    this.layoutStage();
  };

  constructor(parent: HTMLElement) {
    this.root = el('div', 'screen screen-shop', parent);
    this.root.style.display = 'none';

    this.stageHost = el('div', 'shop-stage', this.root);

    // Top strip: back + title | balance.
    const top = el('div', 'shop-top', this.root);
    const titles = el('div', 'shop-titles', top);
    const titleRow = el('div', 'shop-titlerow', titles);
    // M114b: the ONE shared back chevron, inline in the existing title row.
    backButton(titleRow, () => this.cb?.onBack(), { corner: false });
    const title = el('div', 'shop-title', titleRow);
    this.titleEl = title;
    title.textContent = 'THE SHOP';
    title.dataset.text = 'THE SHOP'; // chrome face + extruded edge (CSS, M113)
    this.rideEl = el('div', 'shop-ride', titles);

    // M131 (owner rule): the shop is an SP surface — it shows the ONE
    // currency it spends (SP, Shop Points, green coin) as a balance, and
    // nothing else. XP (unlocks / rank) lives in the garage and on the title.
    const bal = el('div', 'shop-balance', top);
    el('div', 'shop-balance-label', bal).textContent = 'SHOP POINTS';
    const balRow = el('div', 'shop-balance-row', bal);
    const coin = el('span', 'shop-balance-star', balRow);
    coin.innerHTML = SP_COIN_SVG;
    this.balanceEl = el('span', 'shop-balance-value', balRow);
    el('span', 'shop-balance-unit', balRow).textContent = 'SP';
    // M130 — GET SP: the packs sheet. Hidden unless the callbacks allow it.
    this.getSpBtn = btn('GET SP', 'btn shop-getsp', bal, () => this.openSpSheet());
    this.getSpBtn.style.display = 'none';
    this.spSheet = el('div', 'shop-sp-sheet', this.root);
    this.spSheet.style.display = 'none';
    this.spSheet.addEventListener('click', (e) => {
      if (e.target === this.spSheet) this.closeSpSheet();
    });

    // M102 — the PURCHASED pop: a one-beat confirmation that the buy landed
    // and what it cost (the silent balance tick was easy to miss, owner note).
    this.popEl = el('div', 'shop-pop', this.root);

    // M113 — the LEFT detail dock: the browsed item's name / rarity / kind /
    // desc + the action button, parked beside the car (under it in portrait).
    const dock = el('div', 'shop-detaildock', this.root);
    this.dockEl = dock;
    const dHead = el('div', 'shop-detail-head', dock);
    this.detailName = el('div', 'shop-detail-name', dHead);
    this.detailType = el('span', 'shop-type', dHead);
    this.detailRarity = el('span', 'shop-rarity', dHead);
    this.detailDesc = el('div', 'shop-detail-desc', dock);
    this.numPickEl = el('div', 'shop-numpick', dock);
    this.numPickEl.style.display = 'none';
    this.actionBtn = btn('', 'btn shop-action', dock, () => this.onAction());

    // M113 — the RIGHT panel (portrait: bottom sheet): big icon-tile tabs,
    // the SORT row, then the scrollable item GRID.
    const panel = el('div', 'shop-panel', this.root);
    this.panelEl = panel;
    this.tabsEl = el('div', 'shop-tabs', panel);
    // M87: the PAINT JOBS inspiration filter row (ALL · KITS · FLAGS).
    this.filtersEl = el('div', 'shop-filters', panel);
    this.railEl = el('div', 'shop-rail', panel);

    this.unsubViewport = subscribeViewport(this.onWindowResize);
  }

  /** `vehicleId` (M91): the car this shop visit DRESSES — the garage passes
   *  its browsed ride, so what you see is what gets equipped. Falls back to
   *  the saved ride (direct/legacy entries). */
  show(save: SaveData, cb: ShopCallbacks, vehicleId?: string): void {
    this.save = save;
    this.cb = cb;
    this.forVehicle = vehicleId || save.settings.playerVehicle || 'coupe';
    this.tab = 'paintjobs';
    this.filter = 'all';
    this.pendingNumber = null;
    this.pendingStyle = null;
    this.selected = this.equippedInTab();
    this.disarmConfirm();
    // The game's reduced-motion SETTING stills the pop/pulse layers (the OS
    // media query covers the rest — the M78 pattern).
    this.root.classList.toggle('reduced', save.settings.reducedMotion);

    if (!this.stage) this.stage = new GarageStage(this.stageHost);
    const veh = this.vehicleId();
    this.rideEl.textContent = `GARAGE · ${vehicleLabel(veh).toUpperCase()}`;
    this.stage.setVehicles([{ id: veh, unlocked: true, look: this.previewLook() }]);
    this.stage.setHero(veh);
    this.stage.setRingScale(SHOP_RING_SCALE);
    this.stage.show();

    this.renderBalance(false);
    // Owner round (2026-10-02): the game is not owned — the lock banner tops
    // the item panel (browsing + previewing stay open) and SP is not sold.
    this.lockEl?.remove();
    this.lockEl = null;
    // M139: the signed-out PROMO face (see ShopCallbacks.promo).
    this.stopPromo();
    this.promo?.root.remove();
    this.promo = null;
    const promo = cb.promo?.() ?? null;
    this.promoOn = !!promo;
    this.root.classList.toggle('shop-promo', this.promoOn);
    const pageTitle = promo?.focus === 'customize' ? 'CUSTOMIZE' : 'THE SHOP';
    this.titleEl.textContent = pageTitle;
    this.titleEl.dataset.text = pageTitle;
    if (promo) {
      this.promo = promoPanel(this.root, { ...promo, className: 'shop-promopanel' });
    }
    const lock = promo ? null : (cb.gameLock?.() ?? null);
    if (lock) {
      this.lockEl = lockCta(this.panelEl, { ...lock, className: 'shop-lockcta' });
      this.panelEl.insertBefore(this.lockEl, this.panelEl.firstChild);
    }
    this.getSpBtn.style.display = this.canSellSp() && !lock ? '' : 'none';
    this.closeSpSheet();
    this.renderTabs();
    this.renderRail();
    this.railEl.scrollTop = 0; // the grid persists across visits — start at top
    this.renderDetail();

    this.root.style.display = '';
    requestAnimationFrame(() => {
      this.stage?.resize();
      this.layoutStage();
    });
    if (promo) this.startPromo(promo.focus, save.settings.reducedMotion);
  }

  /** M139 — run the reel: each featured look dresses the car (M147: the
   *  panel no longer names it — owner removed NOW SHOWING). Reduced motion:
   *  the first look holds (no autoplay). */
  private startPromo(focus: 'shop' | 'customize', reduced: boolean): void {
    const reel = PROMO_REEL[focus].filter(
      (p) =>
        !!cosmeticById(p.feature) &&
        Object.values(p.loadout)
          .flat()
          .every((id) => typeof id !== 'string' || !!cosmeticById(id)),
    );
    if (reel.length === 0) return;
    let i = 0;
    const show = (): void => {
      const p = reel[i % reel.length];
      this.stage?.setLook(this.vehicleId(), resolveLook(p.loadout));
      i += 1;
    };
    show();
    if (!reduced) this.promoTimer = window.setInterval(show, PROMO_LOOK_MS);
  }

  private stopPromo(): void {
    if (this.promoTimer !== null) {
      window.clearInterval(this.promoTimer);
      this.promoTimer = null;
    }
  }

  hide(): void {
    this.stopPromo();
    this.closeSpSheet();
    this.disarmConfirm();
    this.cancelCount();
    this.cancelCount = () => {};
    this.popEl.classList.remove('show');
    if (this.popTimer !== null) {
      window.clearTimeout(this.popTimer);
      this.popTimer = null;
    }
    this.stage?.hide();
    this.root.style.display = 'none';
  }

  dispose(): void {
    this.unsubViewport?.();
    this.stage?.dispose();
    this.root.remove();
  }

  // ----- state helpers -------------------------------------------------------

  /** The vehicle this shop visit dresses (M91 — set per show, never drifts). */
  private forVehicle = 'coupe';

  private vehicleId(): string {
    return this.forVehicle;
  }

  /** What the CURRENT tab's slot is wearing ('paintjobs' = the merged body
   *  slot — kit OR flag, whichever is on). */
  private equippedInTab(): string | null {
    if (!this.save) return null;
    if (this.tab === 'paintjobs') return equippedPaintJob(this.save, this.vehicleId());
    if (this.tab === 'tires') return loadoutFor(this.save, this.vehicleId()).tires ?? null;
    return null;
  }

  private owned(id: string): boolean {
    return !!this.save?.cosmetics.owned.includes(id);
  }

  /** The loadout to PREVIEW: what's equipped, with the browsed item overlaid
   *  in its slot (or that slot stripped for the STOCK card). PAINT JOBS is
   *  the merged body slot — previewing a kit clears the flag and vice versa,
   *  mirroring equip (M87). */
  private previewLook(): ReturnType<typeof resolveLook> {
    if (!this.save) return undefined;
    const loadout = { ...loadoutFor(this.save, this.vehicleId()) };
    if (this.tab === 'paintjobs') {
      delete loadout.paint;
      delete loadout.wrap;
      const item = this.selected ? cosmeticById(this.selected) : null;
      if (item && (item.category === 'paint' || item.category === 'wrap')) {
        loadout[item.category] = item.id;
      }
    } else if (this.tab === 'tires') {
      if (this.selected === null) delete loadout.tires;
      else loadout.tires = this.selected;
    } else if (this.tab === 'mods') {
      // M128: mods stack — preview = what's worn plus the browsed one.
      const mods = equippedMods(this.save, this.vehicleId());
      if (this.selected && !mods.includes(this.selected)) mods.push(this.selected);
      if (mods.length > 0) loadout.mods = mods;
      else delete loadout.mods;
      if (this.pendingNumber !== null) loadout.kitNumber = this.pendingNumber;
      if (this.pendingStyle !== null) loadout.tifoStyle = this.pendingStyle;
    }
    return resolveLook(loadout);
  }

  private refreshPreview(): void {
    if (this.promoOn) return; // M139: the promo reel owns the car's look
    this.stage?.setLook(this.vehicleId(), this.previewLook());
  }

  // ----- rendering -----------------------------------------------------------

  private renderBalance(animate: boolean, from = 0): void {
    if (!this.save) return;
    const bal = spBalance(this.save);
    this.cancelCount();
    if (animate && !this.save.settings.reducedMotion) {
      this.cancelCount = animateCount(this.balanceEl, from, bal, 700, false);
    } else {
      this.balanceEl.textContent = String(bal);
      this.cancelCount = (): void => {};
    }
  }

  /** M102 — the PURCHASED pop: item name, what it cost, the new green BANK.
   *  One at a time; auto-dismisses; hide() clears it so it can't stick. */
  private showPurchasePop(item: CosmeticItem): void {
    if (!this.save) return;
    this.popEl.replaceChildren();
    el('div', 'shop-pop-kicker', this.popEl).textContent = 'PURCHASED';
    el('div', 'shop-pop-name', this.popEl).textContent = item.name.toUpperCase();
    const row = el('div', 'shop-pop-row', this.popEl);
    el('span', 'shop-pop-cost', row).textContent = `−${item.price} SP`;
    el('span', 'shop-pop-bank', row).textContent = `${spBalance(this.save)} SP LEFT`;
    el('div', 'shop-pop-sub', this.popEl).textContent = 'EQUIPPED TO YOUR RIDE';
    this.popEl.classList.remove('show');
    void this.popEl.offsetWidth; // restart the entrance on back-to-back buys
    this.popEl.classList.add('show');
    if (this.popTimer !== null) window.clearTimeout(this.popTimer);
    this.popTimer = window.setTimeout(() => {
      this.popEl.classList.remove('show');
      this.popTimer = null;
    }, 2600);
  }

  /** M128c — the category tabs are home-page menu cards (catTabs.ts): the
   *  skewed icon zone, italic title, uppercase sub; active = the hero gold. */
  private renderTabs(): void {
    this.tabsEl.replaceChildren();
    for (const t of TABS) {
      catTab(this.tabsEl, {
        key: t.key,
        title: t.label,
        sub: t.sub,
        on: this.tab === t.key,
        onClick: () => {
          if (this.tab === t.key) return;
          this.tab = t.key;
          this.selected = t.key === 'mods' ? null : this.equippedInTab();
          this.disarmConfirm();
          this.renderTabs();
          this.renderRail();
          this.railEl.scrollTop = 0; // a fresh category starts at the top (a
          // card re-select must NOT reset the grid scroll — only tab/sort do)
          this.renderDetail();
          this.refreshPreview();
          this.layoutStage(); // tires zoom right in on the wheels; others pull back
        },
      });
    }
  }

  /** One card per item (a STOCK card leads each slot, FIFA-style). PAINT JOBS
   *  is the merged kits+flags grid, sorted by the inspiration filter (M87).
   *  M113: the rail is a vertically scrolling GRID inside the docked panel —
   *  it simply renders whatever the catalog lists (no counts pinned). */
  private renderRail(): void {
    const tab = this.tab; // const-capture so TS narrows past the DOM calls
    this.filtersEl.style.display = tab === 'paintjobs' ? '' : 'none';
    this.railEl.replaceChildren();
    this.filtersEl.replaceChildren();
    if (!this.save) return;
    if (tab === 'mods') {
      // M128: mods STACK — the STOCK card means "no mods", every other card
      // shows its own worn state.
      const on = equippedMods(this.save, this.vehicleId());
      this.addCard(null, on.length === 0);
      // M128c (owner): cheapest first — a stable sort keeps catalog order within a tier.
      for (const item of [...cosmeticsFor('mod')].sort((p, q) => p.price - q.price))
        this.addCard(item, on.includes(item.id));
      return;
    }

    if (tab === 'paintjobs') {
      el('span', 'shop-filters-label', this.filtersEl).textContent = 'SORT';
      for (const f of PAINTJOB_FILTERS) {
        btn(f.label, `shop-filter${this.filter === f.key ? ' on' : ''}`, this.filtersEl, () => {
          if (this.filter === f.key) return;
          sfxUiTick();
          this.filter = f.key;
          this.renderRail();
          this.railEl.scrollTop = 0; // fresh sort = back to the top
        });
      }
    }

    const equipped = this.equippedInTab();
    this.addCard(null, equipped === null);
    const items = tab === 'paintjobs' ? paintJobItems(this.filter) : cosmeticsFor('tires');
    for (const item of items) this.addCard(item, equipped === item.id);
  }

  private addCard(item: CosmeticItem | null, isEquipped: boolean): void {
    if (!this.save) return;
    const id = item?.id ?? null;
    const selected = this.selected === id;
    const owned = item ? this.owned(item.id) : true;
    const rarity = item ? RARITY_META[item.rarity] : null;

    const card = btn(
      '',
      `shop-card${selected ? ' selected' : ''}${owned ? ' owned' : ''}`,
      this.railEl,
      () => {
        if (this.selected === id) return;
        sfxUiTick();
        this.selected = id;
        this.disarmConfirm();
        this.renderRail();
        this.renderDetail();
        this.refreshPreview();
      },
    );
    if (rarity) card.style.setProperty('--rarity', rarity.color);
    if (item?.mod && !item.mod.fits.includes(this.vehicleId())) card.classList.add('nofit');

    const art = el('div', 'shop-card-art', card);
    if (!item) {
      art.classList.add('shop-card-stock');
      art.textContent = '—';
    } else {
      cosmeticCardArt(item, art); // kit shirt / flag / tire disc (shared, M87)
    }

    el('div', 'shop-card-name', card).textContent = item ? item.name.toUpperCase() : 'STOCK';

    const foot = el('div', 'shop-card-foot', card);
    if (!item) {
      foot.textContent = isEquipped ? 'EQUIPPED' : 'FREE';
      foot.classList.toggle('equipped', isEquipped);
      foot.classList.toggle('owned', !isEquipped);
    } else if (isEquipped) {
      foot.textContent = 'EQUIPPED';
      foot.classList.add('equipped');
    } else if (owned) {
      foot.textContent = 'OWNED';
      foot.classList.add('owned');
    } else {
      const afford = spBalance(this.save) >= item.price;
      foot.classList.toggle('poor', !afford);
      foot.innerHTML = `<span class="shop-card-star">${SP_COIN_SVG}</span>${item.price}`;
    }
  }

  private renderDetail(): void {
    if (!this.save) return;
    this.numPickEl.style.display = 'none';
    if (this.tab === 'mods') {
      this.renderModDetail();
      return;
    }
    this.actionBtn.style.display = '';
    const item = this.selected ? cosmeticById(this.selected) : null;
    const equipped = this.equippedInTab();

    if (!item) {
      this.detailName.textContent = 'STOCK';
      this.detailRarity.style.display = 'none';
      this.detailType.style.display = 'none';
      this.detailDesc.textContent = 'The factory look. Always yours.';
      this.setAction(
        equipped === null ? 'EQUIPPED' : 'EQUIP',
        equipped === null,
        () => this.equip(null),
        equipped === null ? 'plain' : 'equip',
      );
      return;
    }

    const rarity = RARITY_META[item.rarity];
    this.detailName.textContent = item.name.toUpperCase();
    this.detailType.style.display = '';
    this.detailType.textContent = cosmeticTypeTag(item); // KIT / FLAG / BOOTS
    this.detailRarity.style.display = '';
    this.detailRarity.textContent = rarity.label;
    this.detailRarity.style.setProperty('--rarity', rarity.color);
    this.detailDesc.textContent = item.desc;

    if (equipped === item.id) {
      this.setAction('EQUIPPED', true, () => {});
    } else if (this.owned(item.id)) {
      this.setAction('EQUIP', false, () => this.equip(item.id), 'equip');
    } else if (this.cb?.gameLock?.()) {
      const g = this.cb.gameLock()!;
      this.setAction(`${BUY_GAME_LABEL} · ${g.price}`, false, () => g.onBuy(), 'buy');
    } else if (this.cb?.buyLocked?.()) {
      // M110: guests can't spend SP — buying banks to the ACCOUNT now, so the
      // button is the funnel (gold = affirmative, M92 rule).
      this.setAction('CREATE RACER ID TO BUY', false, () => this.cb?.onCreateAccount?.(), 'buy');
    } else if (spBalance(this.save) < item.price) {
      // M130: where SP is sold, the shortfall line IS the way to fix it.
      const short = item.price - spBalance(this.save);
      if (this.canSellSp())
        this.setAction(`NEED ${short} MORE SP · GET SP`, false, () => this.openSpSheet(), 'buy');
      else this.setAction(`NEED ${short} MORE SP`, true, () => {});
    } else if (this.confirmArmed) {
      this.setAction(`CONFIRM ${item.price} SP?`, false, () => this.buy(item), 'buy');
      this.actionBtn.classList.add('confirming');
    } else {
      this.setAction(`BUY · ${item.price} SP`, false, () => this.armConfirm(), 'buy');
    }
  }

  /** M128 — the MODS tab detail: mods stack (EQUIP / UNEQUIP per item, the
   *  STOCK card clears them all), a mod that has no anchor on this ride says
   *  so instead of equipping badly, and the Kit Number carries its picker. */
  private renderModDetail(): void {
    if (!this.save) return;
    this.actionBtn.style.display = '';
    const veh = this.vehicleId();
    const on = equippedMods(this.save, veh);
    const item = this.selected ? cosmeticById(this.selected) : null;
    if (!item?.mod) {
      this.detailName.textContent = 'STOCK';
      this.detailRarity.style.display = 'none';
      this.detailType.style.display = 'none';
      this.detailDesc.textContent =
        on.length === 0
          ? 'No mods on this ride. Clean and quick.'
          : `${on.length} mod${on.length > 1 ? 's' : ''} on this ride.`;
      this.setAction(on.length === 0 ? 'EQUIPPED' : 'REMOVE ALL MODS', on.length === 0, () =>
        this.equip(null),
      );
      return;
    }
    const rarity = RARITY_META[item.rarity];
    const fits = item.mod.fits.includes(veh);
    this.detailName.textContent = item.name.toUpperCase();
    this.detailType.style.display = '';
    this.detailType.textContent = cosmeticTypeTag(item);
    this.detailRarity.style.display = '';
    this.detailRarity.textContent = rarity.label;
    this.detailRarity.style.setProperty('--rarity', rarity.color);
    const motion = item.mod.motion ? ` ${item.mod.motion}.` : '';
    this.detailDesc.textContent = fits
      ? `${item.desc}.${motion}`
      : `Not made for the ${vehicleLabel(veh)}. ${item.desc}.`;
    if (item.id === MOD_KITNUMBER_ID) this.renderNumberPicker();
    if (item.id === MOD_TIFO_ID) this.renderStylePicker(); // M128c
    if (on.includes(item.id)) {
      this.setAction('UNEQUIP', false, () => this.equip(item.id));
    } else if (this.owned(item.id)) {
      this.setAction(
        fits ? 'EQUIP' : 'NOT FOR THIS RIDE',
        !fits,
        () => this.equip(item.id),
        'equip',
      );
    } else if (this.cb?.gameLock?.()) {
      const g = this.cb.gameLock()!;
      this.setAction(`${BUY_GAME_LABEL} · ${g.price}`, false, () => g.onBuy(), 'buy');
    } else if (this.cb?.buyLocked?.()) {
      this.setAction('CREATE RACER ID TO BUY', false, () => this.cb?.onCreateAccount?.(), 'buy');
    } else if (spBalance(this.save) < item.price) {
      // M130: where SP is sold, the shortfall line IS the way to fix it.
      const short = item.price - spBalance(this.save);
      if (this.canSellSp())
        this.setAction(`NEED ${short} MORE SP · GET SP`, false, () => this.openSpSheet(), 'buy');
      else this.setAction(`NEED ${short} MORE SP`, true, () => {});
    } else if (this.confirmArmed) {
      this.setAction(`CONFIRM ${item.price} SP?`, false, () => this.buy(item), 'buy');
      this.actionBtn.classList.add('confirming');
    } else {
      this.setAction(`BUY · ${item.price} SP`, false, () => this.armConfirm(), 'buy');
    }
  }

  /** M128 — pick your number, 1 to 11 (owner: from a list, in the buy flow).
   *  While browsing it previews; once the mod is owned it persists at once. */
  private renderNumberPicker(): void {
    if (!this.save) return;
    const veh = this.vehicleId();
    renderNumberChips(this.numPickEl, this.pendingNumber ?? kitNumberFor(this.save, veh), (n) => {
      if (!this.save || !this.cb) return;
      this.pendingNumber = n;
      if (this.owned(MOD_KITNUMBER_ID)) {
        this.save = setKitNumber(this.save, veh, n);
        this.cb.onSave(this.save);
      }
      this.renderNumberPicker();
      this.refreshPreview();
    });
  }

  /** M128c — the Fan Tifo's design (smiley / SEE YA! / TOO SLOW!), the same
   *  contract as the number: previews while browsing, persists once owned. */
  private renderStylePicker(): void {
    if (!this.save) return;
    const veh = this.vehicleId();
    renderStyleChips(this.numPickEl, this.pendingStyle ?? tifoStyleFor(this.save, veh), (n) => {
      if (!this.save || !this.cb) return;
      this.pendingStyle = n;
      if (this.owned(MOD_TIFO_ID)) {
        this.save = setTifoStyle(this.save, veh, n);
        this.cb.onSave(this.save);
      }
      this.renderStylePicker();
      this.refreshPreview();
    });
  }

  /** kind drives the colour: BUY/CONFIRM wear Skill-Point gold, EQUIP the
   *  affirmative green, everything else the quiet glass (owner spec). */
  private setAction(
    label: string,
    disabled: boolean,
    fn: () => void,
    kind: 'buy' | 'equip' | 'plain' = 'plain',
  ): void {
    this.actionBtn.textContent = label;
    this.actionBtn.disabled = disabled;
    this.actionBtn.classList.remove('confirming');
    this.actionBtn.classList.toggle('shop-action-buy', kind === 'buy');
    this.actionBtn.classList.toggle('shop-action-equip', kind === 'equip');
    this.action = fn;
  }

  private action: () => void = () => {};
  private getSpBtn!: HTMLButtonElement;
  private spSheet!: HTMLDivElement;
  private spBusy = false;
  /** M134 — the store confirmed the payment; the points are being added. */
  private spPaid = false;
  /** M134 — paid and released: the line the sheet shows until they arrive. */
  private spPendingNote: string | null = null;
  /** M134 — that line is a CONFIRMED payment (green) or an unsure one (amber). */
  private spPendingPaid = true;
  /** M134 — only the NEWEST purchase may repaint the sheet (the player can
   *  close it mid-wait and start another). */
  private spRun = 0;
  /** M133 — the store's prices, once fetched (native app only). */
  private storePrices: Record<string, string> | null = null;

  /** M130 — Flow repaints the BANK after a sync lands bought SP (the
   *  checkout return): same save object, fresh numbers, no re-show. */
  refreshSave(save: SaveData): void {
    // M134: the bought points arrived, so the "on the way" line is done — and
    // a sheet that is only waiting on them closes WITH the toast, not at the
    // next wallet pull a few seconds later. (A sheet still waiting on the
    // store's own purchase sheet, busy and not yet paid, is left alone.)
    if (this.spPendingNote !== null || this.spPaid) {
      this.spPendingNote = null;
      if (!this.spBusy || this.spPaid) {
        // The purchase that was waiting is settled: its own late return must
        // not close a sheet the player has opened again in the meantime.
        this.spRun++;
        this.closeSpSheet();
      }
    }
    if (!this.save || this.root.style.display === 'none') return;
    const before = spBalance(this.save);
    this.save = save;
    this.renderBalance(true, before);
    this.renderRail();
    this.renderDetail();
  }

  private canSellSp(): boolean {
    return typeof this.cb?.onBuySp === 'function';
  }

  /** M130 — the packs sheet: four cards, the one rule in plain words, and
   *  the hand-off to checkout. A signed-out web player is sent to create a
   *  Racer ID first (the wallet is account state), the M110 shop rule. */
  private openSpSheet(): void {
    if (!this.save || !this.canSellSp()) return;
    sfxUiTick();
    this.renderSpSheet(null);
    this.spSheet.style.display = '';
    // M133: inside the store app the prices come from the store itself.
    if (this.cb?.spStorePrices && this.storePrices === null) {
      void this.cb
        .spStorePrices(SP_PACKS_ON_SALE.map((p) => p.id))
        .then((prices) => {
          this.storePrices = prices;
          if (this.spSheet.style.display !== 'none' && !this.spBusy) this.renderSpSheet(null);
        })
        .catch(() => {
          this.storePrices = {};
          if (this.spSheet.style.display !== 'none' && !this.spBusy) this.renderSpSheet(null);
        });
    }
  }

  private closeSpSheet(): void {
    this.spSheet.style.display = 'none';
    this.spBusy = false;
    this.spPaid = false;
  }

  private renderSpSheet(note: string | null): void {
    this.spSheet.innerHTML = '';
    const panel = el('div', 'shop-sp-panel', this.spSheet);
    const head = el('div', 'shop-sp-head', panel);
    el('div', 'shop-sp-kicker', head).textContent = 'THE SHOP · SHOP POINTS';
    const title = el('div', 'shop-sp-title', head);
    title.textContent = 'GET SHOP POINTS';
    title.dataset.text = 'GET SHOP POINTS';
    el('div', 'shop-sp-sub', head).textContent =
      'SP is what the shop spends: kits, wraps, wheels and mods. Rides unlock with XP, and XP is earned by racing only.';
    btn('✕', 'btn shop-sp-close', head, () => this.closeSpSheet()).setAttribute(
      'aria-label',
      'Close',
    );
    const grid = el('div', 'shop-sp-grid', panel);
    const inStore = typeof this.cb?.spStorePrices === 'function';
    const loaded = this.storePrices !== null;
    // Inside the store app a pack is offered only if the store prices it.
    const packs =
      inStore && loaded
        ? SP_PACKS_ON_SALE.filter((p) => this.storePrices?.[p.id] !== undefined)
        : SP_PACKS_ON_SALE;
    if (inStore && loaded && packs.length === 0 && note === null) {
      note = 'The store is not reachable right now. Try again in a moment.';
    }
    for (const p of packs) {
      const card = btn(
        '',
        'shop-sp-card' + (p.featured ? ' featured' : ''),
        grid,
        () => void this.buySp(p.id),
      );
      card.disabled = this.spBusy || (inStore && !loaded);
      el('div', 'shop-sp-label', card).textContent = p.label;
      const amt = el('div', 'shop-sp-amount', card);
      el('span', 'shop-sp-amount-n', amt).textContent = p.sp.toLocaleString('en-US');
      el('span', 'shop-sp-amount-unit', amt).textContent = 'SP';
      el('div', 'shop-sp-price', card).textContent = inStore
        ? (this.storePrices?.[p.id] ?? '…')
        : `$${p.usd}`;
      if (p.bonusPct > 0) el('div', 'shop-sp-bonus', card).textContent = `+${p.bonusPct}% BONUS`;
      el('div', 'shop-sp-blurb', card).textContent = p.blurb;
    }
    // The one line under the packs. M134: a payment that went through is
    // always acknowledged in the SP green, never left to a silent wait.
    const foot = el('div', 'shop-sp-foot', panel);
    let tone: 'plain' | 'error' | 'paid' | 'unsure' = 'plain';
    if (note !== null) {
      foot.textContent = note;
      tone = 'error';
    } else if (this.spBusy) {
      if (!inStore) {
        foot.textContent = 'OPENING SECURE CHECKOUT…';
      } else if (this.spPaid) {
        foot.textContent = 'PAYMENT RECEIVED · ADDING YOUR SHOP POINTS…';
        tone = 'paid';
      } else {
        foot.textContent = 'WAITING FOR THE STORE…';
      }
    } else if (this.spPendingNote !== null) {
      foot.textContent = this.spPendingNote;
      tone = this.spPendingPaid ? 'paid' : 'unsure';
    } else {
      foot.textContent = inStore
        ? 'Paid through your store account · bought SP never expires'
        : 'Secure checkout by Stripe · prices in USD · bought SP never expires';
    }
    foot.classList.toggle('error', tone === 'error');
    foot.classList.toggle('paid', tone === 'paid');
    foot.classList.toggle('unsure', tone === 'unsure');
  }

  private async buySp(packId: string): Promise<void> {
    if (!this.cb?.onBuySp || this.spBusy) return;
    if (this.cb.buyLocked?.()) {
      // Wallets are account state (M110): create the Racer ID first.
      this.closeSpSheet();
      this.cb.onCreateAccount?.();
      return;
    }
    sfxUiTick();
    const run = ++this.spRun;
    this.spBusy = true;
    this.spPaid = false;
    this.renderSpSheet(null);
    const res = await this.cb.onBuySp(packId, () => {
      if (run !== this.spRun) return;
      this.spPaid = true;
      if (this.spBusy) this.renderSpSheet(null);
    });
    if (res === null) return; // the page is navigating to Stripe; stay busy
    if (run !== this.spRun) return; // a newer purchase owns the sheet now
    this.spBusy = false;
    this.spPaid = false;
    if (typeof res === 'string') {
      this.renderSpSheet(res);
    } else if (res.status === 'done') {
      // M133: paid in the store and the points are in. Flow's toast + balance
      // repaint have fired; the sheet has done its job.
      this.closeSpSheet();
    } else if (res.status === 'pending') {
      // M134: paid (or possibly paid) and still on the way. Say so and stay
      // open; refreshSave closes the sheet when the points land.
      this.spPendingNote = res.note;
      this.spPendingPaid = res.paid;
      this.renderSpSheet(null);
    } else {
      this.renderSpSheet(null); // the player closed the store's sheet
    }
  }
  private onAction(): void {
    if (this.promoOn) return; // M139: the promo face has no item actions
    this.action();
  }

  // ----- buy / equip ---------------------------------------------------------

  /** Two-tap buy (racing-game convention — no modal): BUY arms, CONFIRM pays. */
  private armConfirm(): void {
    this.confirmArmed = true;
    this.renderDetail();
    this.confirmTimer = window.setTimeout(() => this.disarmConfirm(true), 3500);
  }

  private disarmConfirm(rerender = false): void {
    this.confirmArmed = false;
    if (this.confirmTimer !== null) {
      window.clearTimeout(this.confirmTimer);
      this.confirmTimer = null;
    }
    if (rerender) this.renderDetail();
  }

  private buy(item: CosmeticItem): void {
    if (!this.save || !this.cb || this.promoOn) return;
    if (this.cb.buyLocked?.()) return; // M110: hard guard behind the UI gate
    if (this.cb.gameLock?.()) return; // the game itself is not owned
    const before = spBalance(this.save);
    const res = purchaseCosmetic(this.save, item.id);
    this.disarmConfirm();
    if (!res.ok) {
      this.renderDetail(); // stale state (funds/owned) — repaint the truth
      return;
    }
    // Purchases auto-equip — you buy it to wear it (FIFA convention).
    const veh = this.vehicleId();
    if (item.mod) {
      // M128: a mod goes on only where it fits; the picked number lands too.
      this.save = item.mod.fits.includes(veh) ? equipMod(res.save, veh, item.id, true) : res.save;
      if (item.id === MOD_KITNUMBER_ID && this.pendingNumber !== null)
        this.save = setKitNumber(this.save, veh, this.pendingNumber);
      if (item.id === MOD_TIFO_ID && this.pendingStyle !== null)
        this.save = setTifoStyle(this.save, veh, this.pendingStyle);
    } else {
      this.save = equipCosmetic(res.save, veh, item.category, item.id);
    }
    this.cb.onSave(this.save);
    sfxCollect();
    this.showPurchasePop(item);
    this.renderBalance(true, before);
    this.balanceEl.classList.remove('spent');
    void this.balanceEl.offsetWidth;
    this.balanceEl.classList.add('spent');
    this.renderRail();
    this.renderDetail();
    this.refreshPreview();
  }

  private equip(itemId: string | null): void {
    if (!this.save || !this.cb || this.promoOn) return;
    if (this.tab === 'mods') {
      // M128: mods stack — STOCK clears them all, a card toggles its own.
      const veh = this.vehicleId();
      this.save =
        itemId === null
          ? clearMods(this.save, veh)
          : equipMod(this.save, veh, itemId, !equippedMods(this.save, veh).includes(itemId));
    } else if (itemId === null) {
      // STOCK: clear the whole slot — for PAINT JOBS that's BOTH body
      // categories (the merged slot, M87).
      this.save =
        this.tab === 'paintjobs'
          ? unequipPaintJob(this.save, this.vehicleId())
          : equipCosmetic(this.save, this.vehicleId(), 'tires', null);
    } else {
      const item = cosmeticById(itemId);
      if (!item) return;
      // The item's own catalog category — equipCosmetic swaps the sibling
      // body slot out automatically (kit ↔ flag exclusivity).
      this.save = equipCosmetic(this.save, this.vehicleId(), item.category, itemId);
    }
    this.cb.onSave(this.save);
    sfxUiTick();
    this.renderRail();
    this.renderDetail();
    this.refreshPreview();
  }

  // ----- stage layout --------------------------------------------------------

  /** M113/M114/M115 — park the car in the clear zone beside/above the docked
   *  panel: LEFT of the right-docked panel on desktop + landscape (the M95
   *  split, now at every landscape height), TOP of the screen over the bottom
   *  sheet in portrait.
   *
   *  PAINT JOBS frames the whole car close (fov zoom at every aspect — the
   *  ground-anchored camera can't park a BIG car above the portrait dock,
   *  nearer anchors slide behind it; measured M113), and on desktop it sits at
   *  the derived faux-centre of the clear zone (M115).
   *
   *  TIRES & RIMS: on PHONES it is a still, ultra-tight close-up of ONE wheel
   *  (M115) — with a graceful fall-through to the M114 whole-car-at-hub-height
   *  framing when the model has no named wheels; on desktop/tall landscape,
   *  where there is room to read the whole car, it keeps that M114 framing. */
  private layoutStage(): void {
    if (!this.stage) return;
    const w = this.root.clientWidth;
    const h = this.root.clientHeight;
    if (w <= 0 || h <= 0) return;
    const portrait = h > w;
    const short = !portrait && h <= 500;
    const phone = portrait || short;
    const tires = this.tab === 'tires';
    const a = portrait ? CAR_PORTRAIT : short ? CAR_SHORT : CAR_LAND;

    // M139: the PROMO face — no grid or dock, so the car takes the whole left
    // of the page, bigger (the looks ARE the sell). Portrait: it stands on
    // the strip above the promo panel's live top edge.
    if (this.promoOn) {
      this.stage.setWheelCloseUp(this.vehicleId(), null);
      this.stage.setLookY(1.3);
      if (portrait) {
        const top = this.promo?.root.getBoundingClientRect().top ?? h * 0.55;
        const p = portraitCarAnchor(h, top > 0 ? top : null);
        this.stage.setFovZoom(p.zoom);
        this.stage.applyTargets({ [this.vehicleId()]: { xFrac: p.x, yFrac: p.y } });
        return;
      }
      this.stage.setFovZoom(short ? 2.0 : 1.75);
      this.stage.applyTargets({
        [this.vehicleId()]: { xFrac: short ? 0.28 : 0.32, yFrac: short ? 0.62 : 0.7 },
      });
      return;
    }

    if (tires && phone) {
      // M132: portrait derives the wheel's band from the live dock edge so
      // the tyre never sinks under the dock on a short viewport.
      const dockTop = this.dockEl.getBoundingClientRect().top;
      const t = portrait ? portraitWheelAnchor(h, dockTop > 0 ? dockTop : null) : PHONE_WHEEL_SHORT;
      if (this.stage.setWheelCloseUp(this.vehicleId(), t)) return;
      // else: no named wheels on this model — fall through to whole-car.
    } else {
      this.stage.setWheelCloseUp(this.vehicleId(), null);
    }

    // Camera moves land BEFORE the layout pass (the M113b rule) so the
    // anchor unprojects through the frame the player will actually see.
    this.stage.setLookY(tires ? WHEEL_LOOK_Y : 1.3);
    // M128: the MODS tab pulls back a touch — tifo poles and pylons are tall.
    if (portrait && !tires) {
      // M132: the car stands on the strip ABOVE the live detail dock (its
      // wheels used to hide under it) at a zoom that fits the body inside a
      // phone's width — shared with the garage CUSTOMIZE (dockLayout.ts).
      const dockTop = this.dockEl.getBoundingClientRect().top;
      const p = portraitCarAnchor(h, dockTop > 0 ? dockTop : null);
      this.stage.setFovZoom(this.tab === 'mods' ? p.zoom * 0.82 : p.zoom);
      this.stage.applyTargets({ [this.vehicleId()]: { xFrac: p.x, yFrac: p.y } });
      return;
    }
    this.stage.setFovZoom(tires ? a.zoomT : this.tab === 'mods' ? a.zoom * 0.82 : a.zoom);
    const x = phone ? (tires ? a.xT : a.x) : fauxCentreX(w);
    this.stage.applyTargets({
      [this.vehicleId()]: { xFrac: x, yFrac: tires ? a.yT : a.y },
    });
  }
}
